import React, { useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { message } from 'antd';
import { setAuth } from '../../utils/auth';

function AuthHandler() {
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const jwtToken = params.get('token');
    const userId: any = params.get('userId');

    if (jwtToken) {
      setAuth(jwtToken, userId);
      localStorage.setItem('userEmail', params.get('email') || '');

      const isNew = params.get('isNew') === 'true';
      const needsVerification = params.get('needsVerification') === 'true';

      // Check for campaign flag (set in Login.tsx with TTL before OAuth redirect).
      // Stale flags (>30 min old) auto-expire so unrelated future logins don't
      // get forced into Stripe checkout.
      let storedCampaign: string | null = null;
      const raw = localStorage.getItem('campaign');
      if (raw) {
        try {
          const parsed = JSON.parse(raw);
          if (parsed?.value && parsed?.expiresAt && parsed.expiresAt > Date.now()) {
            storedCampaign = parsed.value;
          } else {
            localStorage.removeItem('campaign');
          }
        } catch {
          localStorage.removeItem('campaign');
        }
      }
      const campaign = storedCampaign || params.get('campaign');

      if (campaign === 'promo30') {
        // Create a campaign Stripe Checkout Session (30-day Pro trial).
        // Clear the campaign flag only on definitive outcomes (success / already subscribed),
        // so transient errors leave the flag intact for retry.
        axios
          .post(
            `${process.env.REACT_APP_SERVER_URL}/campaign/checkout-session`,
            { userId },
            { headers: { Authorization: `Bearer ${jwtToken}` } }
          )
          .then((res) => {
            if (res.data?.checkoutUrl) {
              localStorage.removeItem('campaign');
              // Use replace() instead of href so the /auth-callback entry is swapped out of
              // browser history. Browser-back from Stripe checkout skips /auth-callback and
              // lands on /offers instead of looping through AuthHandler → /onboarding.
              window.location.replace(res.data.checkoutUrl);
            } else {
              // Fallback: redirect to dashboard
              navigate('/', { replace: true });
            }
          })
          .catch((err) => {
            const data = err?.response?.data;
            const status = err?.response?.status;

            // 409 = user already has an active or trialing subscription
            if (status === 409 && data?.alreadySubscribed) {
              localStorage.removeItem('campaign');
              message.info(data.message || 'You already have an active plan. Redirecting to your dashboard.', 5);
              navigate('/', { replace: true });
              return;
            }

            // Server responded with a non-retryable error (4xx/5xx). Clear the flag
            // so subsequent normal logins don't keep retrying a permanently failing campaign.
            if (status && status >= 400) {
              localStorage.removeItem('campaign');
              console.error('[Campaign] Checkout failed (non-retryable, flag cleared):', err);
              navigate('/', { replace: true });
              return;
            }

            // No response from server (network/timeout). Keep flag so retry can work.
            console.error('[Campaign] Transient error, keeping campaign flag for retry:', err);
            navigate('/', { replace: true });
          });

        return; // Don't run the normal redirect logic below
      }

      if (isNew && needsVerification) {
        // New user — verify email before onboarding
        navigate('/verify-email', { replace: true });
      } else if (isNew) {
        // New user with no verification required (fallback) — go to onboarding
        navigate('/onboarding', { replace: true });
      } else {
        // Returning user — go straight to the app
        navigate('/', { replace: true });
      }
    } else {
      // If there's no token, redirect to login page
      navigate('/login', { replace: true });
    }
  }, [location.search, navigate]);
  return <div></div>;
}

export default AuthHandler;
